import { Component, inject, computed } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { LayoutService } from '../../services/layout.service';
import { PermissionService } from '../../core/services/permission.service';
import { NavigationService } from '../../core/services/navigation.service';
import { MenuItem } from '../../core/models/menu-item.model';

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [NgClass, NgTemplateOutlet, RouterLink, RouterLinkActive, TooltipModule, RippleModule],
    templateUrl: './sidebar.component.html'
})
export class SidebarComponent {
    layoutService = inject(LayoutService);
    private permissionService = inject(PermissionService);
    private navigationService = inject(NavigationService);

    // Sidebar states
    isCollapsed = this.layoutService.isCollapsed;
    isMobileOpen = this.layoutService.isMobileOpen;

    private filterItems(items: MenuItem[]): MenuItem[] {
        return items
            .filter(item => !item.permission || this.permissionService.has(item.permission))
            .map(item => item.children
                ? { ...item, children: this.filterItems(item.children) }
                : item
            )
            // A group with permission-less children (e.g. "Employee") only earns its
            // spot in the nav if at least one child survived the filter above —
            // otherwise it renders as an empty, unusable group header.
            .filter(item => !item.children || item.children.length > 0);
    }
    
    visibleMenuItems = computed(() => {
        this.permissionService.scopes(); // reactive dependency
        return this.filterItems(this.navigationService.menuItems());
    });

    menuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-th-large',
            path: '/dashboard',
        },
        {
            label: 'Employee',
            icon: 'pi pi-users',
            children: [
                { label: 'Directory', icon: 'pi pi-user-plus', path: '/employees' },
                { label: 'Pending Joiners', icon: 'pi pi-check-square', path: '/employees/approvals' }
            ],
        },
        {
            label: 'Leave',
            icon: 'pi pi-calendar-plus',
            permission: 'LEAVE_MODULE',
            children: [
                { label: 'Leave Applications', icon: 'pi pi-file-edit', path: '/leave/applications' },
                { label: 'Leave Approvals', icon: 'pi pi-check-square', path: '/leave/approvals', permission: 'enable_leave_approvals_manage' },
                { label: 'Leave Entitlements', icon: 'pi pi-id-card', path: '/leave/entitlements' },
                { label: 'Leave Types', icon: 'pi pi-cog', path: '/leave/types' },
            ]
        },
        {
            label: 'Attendance',
            icon: 'pi pi-clock',
            permission: 'ATTENDANCE_MODULE',
            children: [
                { label: 'Shifts', icon: 'pi pi-sun', path: '/attendance/shifts' },
                { label: 'Holiday Calendar', icon: 'pi pi-star', path: '/attendance/holiday-calendars' },
                { label: 'Daily Attendance', icon: 'pi pi-check-square', path: '/attendance/daily-attendance' },
                { label: 'Attendance Reports', icon: 'pi pi-chart-bar', path: '/attendance/reports' },
                { label: 'My Attendance', icon: 'pi pi-calendar-clock', path: '/attendance/my-attendance' },
            ],
        },
        {
            label: 'Benefits',
            icon: 'pi pi-shield',
            permission: 'access_benefit_module',
            children: [
                { label: 'Benefit Plans',    icon: 'pi pi-list',         path: '/benefits/plans' },
                { label: 'Enrolments',       icon: 'pi pi-id-card',      path: '/benefits/enrolments' },
                { label: 'Claim Approvals',  icon: 'pi pi-check-circle', path: '/benefits/claim-approvals',  permission: 'enable_benefit_claims_manage' },
                { label: 'My Benefits',      icon: 'pi pi-heart',        path: '/benefits/my-benefits' },
                // { label: 'My Dependants',    icon: 'pi pi-users',        path: '/benefits/my-dependants' }
            ]
        },
        {
            label: 'Recruitment',
            icon: 'pi pi-briefcase',
            permission: 'RECRUITMENT_MODULE',
            children: [
                { label: 'Vacancies', icon: 'pi pi-list', path: '/recruitment/vacancies' },
                { label: 'Talent Pool', icon: 'pi pi-users', path: '/recruitment/talent-pool',permission: 'TALENTPOOL_VIEW'   },
                { label: 'Examinations', icon: 'pi pi-file-edit', path: '/recruitment/examinations' }
            ]
        },
        {
            label: 'Performance',
            icon: 'pi pi-id-card',
            permission: 'access_performance_module',
            children: [
                { label: 'Questions', icon: 'pi pi-bullseye', path: '/performance/criteria' },
                { label: 'Templates', icon: 'pi pi-list', path: '/performance/appraisal-templates' },
                { label: 'Review Cycles', icon: 'pi pi-sync', path: '/performance/appraisal-cycles' },
                { label: 'Reviews', icon: 'pi pi-chart-bar', path: '/performance/evaluations' },
            ],
        },
        {
            label: 'Assets',
            icon: 'pi pi-box',
            permission: 'access_asset_module',
            children: [
                { label: 'Asset Directory', icon: 'pi pi-th-large',               path: '/asset/asset-directory', permission: 'enable_asset_manage'           },
                { label: 'Allocations',     icon: 'pi pi-arrow-right-arrow-left', path: '/asset/allocations',     permission: 'view_asset_allocations'        },
                { label: 'Asset Category',  icon: 'pi pi-list',                   path: '/asset/asset-category',  permission: 'enable_asset_category_manage'  },
                { label: 'Asset Types',     icon: 'pi pi-tag',                    path: '/asset/asset-types',     permission: 'enable_asset_type_manage'      },
                { label: 'Approvals',       icon: 'pi pi-check-circle',           path: '/asset/approvals',       permission: 'enable_asset_approvals_manage' },
                { label: 'Vendors',         icon: 'pi pi-building',               path: '/asset/vendors',         permission: 'enable_asset_vendor_manage'    },
                { label: 'Reports',         icon: 'pi pi-chart-bar',              path: '/asset/reports',         permission: 'view_asset_reports'            },
                { label: 'My Assets',       icon: 'pi pi-user',                   path: '/asset/my-assets'        },
            ]
        },
        {
            label: 'Letters',
            icon: 'pi pi-file-text',
            children: [
                { label: 'Categories', icon: 'pi pi-list', path: '/letter/categories', permission: 'LETTER_MODULE:CATEGORIES' },
                { label: 'Templates', icon: 'pi pi-file-edit', path: '/letter/templates', permission: 'LETTER_MODULE:TEMPLATES' },
                { label: 'Issued Letters', icon: 'pi pi-send', path: '/letter/issued', permission: 'LETTER_MODULE:ISSUE' },
                { label: 'Requests', icon: 'pi pi-inbox', path: '/letter/requests', permission: 'LETTER_MODULE:REQUEST' },
                { label: 'Approvals', icon: 'pi pi-verified', path: '/letter/approvals', permission: 'LETTER_MODULE:APPROVAL' },
                { label: 'Settings', icon: 'pi pi-cog', path: '/letter/settings', permission: 'LETTER_MODULE:SETTINGS' }
              ]
        },
        {
            label: 'Compensation',
            icon: 'pi pi-wallet',
            children: [
                { label: 'Pay Components', icon: 'pi pi-list', path: '/compensation/pay-components'},
                { label: 'Salary Structures', icon: 'pi pi-table', path: '/compensation/salary-structures'},
                { label: 'Formulas', icon: 'pi pi-code', path: '/compensation/formulas'},
                { label: 'CTC Templates', icon: 'pi pi-calculator', path: '/compensation/ctc-templates'},
            ]
        },
        {
            label: 'Dev Tools',
            icon: 'pi pi-wrench',
            permission: 'access_dev_tools',
            children: [
                { label: 'Component Gallery', icon: 'pi pi-palette', path: '/components' },
            ],
        },
    ];

    toggleExpand(item: MenuItem) {
        if (item.children) {
            item.expanded = !item.expanded;
        }
    }

    onToggleCollapse() {
        this.layoutService.toggleCollapse();
    }

    toggleMobile() {
        this.layoutService.toggleMobile();
    }

}